import React from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheckIcon, HandCoinsIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

/**
 * Visual category for a financing-context field requirement.
 *
 * - `required`    — hard requirement when the field is used inside
 *                   the financing flow. Renders in red to signal a
 *                   blocker.
 * - `recommended` — soft requirement. Lenders may price the offer
 *                   differently (or not approve at all) when it's
 *                   missing, but the application can still go out.
 *                   Renders in amber.
 *
 * The two variants are exhaustive on purpose — fields that are
 * truly optional for both CRM and financing don't get a chip at
 * all (the absence is the signal).
 */
export type FinancingFieldBadgeVariant = 'required' | 'recommended';

export interface FinancingFieldBadgeProps {
  /** Required (hard, red) vs recommended (soft, amber). */
  variant: FinancingFieldBadgeVariant;
  /**
   * Whether the underlying field currently carries a value (file
   * uploaded, text typed, etc). When `true` the chip flips to a
   * green "Provided for financing" pill — regardless of `variant`.
   *
   * Keeps the colour state in one place: every consumer just feeds
   * "is this filled?" in and the chip handles required-vs-provided
   * visuals uniformly.
   *
   * Defaults to `false` (the chip stays in the missing-state colour).
   */
  satisfied?: boolean;
  /**
   * Whether to draw the small coin icon. Defaults to `true` because
   * the icon is what visually anchors "this is about financing" —
   * without it the chip just looks like a generic Required/Optional
   * marker. Pass `false` in already-decorated contexts (e.g. inside
   * a section header that's clearly under a "Financing" heading).
   */
  showIcon?: boolean;
  /** Override the rendered label (defaults to the i18n translation). */
  label?: string;
  className?: string;
}

/**
 * `FinancingFieldBadge` — small inline chip rendered next to a
 * field label to communicate that the field is needed (or
 * recommended) for the **financing** flow specifically.
 *
 * The same form may be shown in two contexts:
 *  - CRM workspace — every field is optional; the carrier is
 *    just typing CRM data.
 *  - Financing apply form — some fields are hard-required because
 *    the lender needs them.
 *
 * Rather than building two separate forms, the form accepts a
 * `displayFor` prop and decorates each field with this badge so
 * the user can see *why* a field matters even when it's not
 * strictly required by the current flow.
 *
 * Tri-state colour driven by `variant` + `satisfied`:
 *  - `required`    + missing  → red    "Required for financing"
 *  - `recommended` + missing  → emerald "Recommended for financing"
 *  - `*`           + provided → green  "Provided for financing"
 *
 * Visually small enough to sit inline with a `<Label>` without
 * pushing the input down — it's `text-[11px]` with tight padding,
 * matches the existing requirement card chips.
 */
export function FinancingFieldBadge({
  variant,
  satisfied = false,
  showIcon = true,
  label,
  className,
}: FinancingFieldBadgeProps) {
  const { t } = useTranslation('c-main');

  const state: ChipState = satisfied ? 'provided' : variant;

  const resolvedLabel = label ?? t(LABEL_KEYS[state]);
  const styles = STYLES[state];
  const Icon = state === 'provided' ? CircleCheckIcon : HandCoinsIcon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        styles,
        className,
      )}
      // Title doubles as a tooltip — useful when the chip is
      // pinned next to a dense form label and the user can't tell
      // at a glance which field the chip annotates.
      title={resolvedLabel}>
      {showIcon ? <Icon className="size-3" /> : null}
      {resolvedLabel}
    </span>
  );
}

/* -------------------------------------------------------------- */
/* Internal state table                                            */
/* -------------------------------------------------------------- */

type ChipState = FinancingFieldBadgeVariant | 'provided';

/**
 * State → Tailwind classes.
 *
 * - `required`    → red to read as a blocker ("this is missing
 *                   and the lender will reject without it").
 * - `recommended` → emerald subtly tinted; reading the financing
 *                   palette's primary green ties the chip to the
 *                   surface that benefits from it.
 * - `provided`    → bolder emerald — distinct from `recommended`
 *                   via slightly stronger background so "this is
 *                   done" jumps out over "this is suggested".
 */
const STYLES: Record<ChipState, string> = {
  required: 'bg-red-50 text-red-700',
  recommended: 'bg-emerald-50 text-emerald-700',
  provided: 'bg-emerald-100 text-emerald-800',
};

const LABEL_KEYS: Record<ChipState, string> = {
  required: 'financing_field.required_for_financing',
  recommended: 'financing_field.recommended_for_financing',
  provided: 'financing_field.provided_for_financing',
};
